/**
 * SWISH'IN — progressive enhancements.
 *
 * Everything here is additive: if this file fails to load the page still
 * renders and every link, form and button keeps working. Each feature is
 * isolated in its own IIFE so a throw in one cannot take down the others.
 */

const SW_ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

const swSafe = (name, fn) => {
  try {
    fn();
  } catch (error) {
    console.warn(`[swishin] ${name} failed`, error);
  }
};

const swReady = (fn) => {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fn, { once: true });
  } else {
    fn();
  }
};

/**
 * Pulls the bare card out of a Section Rendering API response.
 *
 * That endpoint always wraps its output in `<div id="shopify-section-…">`.
 * Injecting that wrapper inside another section nests one shopify-section
 * inside another, which the theme editor reports as "broken HTML" and which
 * breaks its section morphing. Returning just the card keeps the DOM honest.
 */
const swCardMarkup = (html) => {
  if (!html || !html.trim()) return '';

  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const card = doc.querySelector('.sw-pcard');
    return card ? card.outerHTML : '';
  } catch {
    return '';
  }
};

/* ==========================================================================
   Wishlist — shared localStorage store used by product cards and the PDP
   ========================================================================== */

const SW_WISHLIST_KEY = 'sw:wishlist';

const swWishlistRead = () => {
  try {
    const value = JSON.parse(localStorage.getItem(SW_WISHLIST_KEY) || '[]');
    // Handles only. Early builds stored numeric ids, which cannot be turned
    // back into product pages — drop them rather than render broken cards.
    return Array.isArray(value) ? value.filter((h) => typeof h === 'string' && h && !/^\d+$/.test(h)) : [];
  } catch {
    return [];
  }
};

const swWishlistWrite = (list) => {
  try {
    localStorage.setItem(SW_WISHLIST_KEY, JSON.stringify(list));
  } catch {
    /* storage unavailable (private mode, quota) — the UI still toggles for this page view */
  }
};

swSafe('wishlist', () => {
  const paint = () => {
    const list = swWishlistRead();

    document.querySelectorAll('[data-sw-wish]').forEach((btn) => {
      const saved = list.includes(btn.dataset.swWish);
      btn.setAttribute('aria-pressed', saved ? 'true' : 'false');

      const text = btn.querySelector('[data-sw-wish-text]');
      if (text) {
        const label = saved ? btn.dataset.swWishLabelSaved : btn.dataset.swWishLabel;
        if (label) text.textContent = label;
      }
    });

    // Header bubble. Hidden at zero so an empty wishlist shows a plain heart.
    document.querySelectorAll('[data-sw-wish-count]').forEach((el) => {
      el.textContent = String(list.length);
      el.hidden = list.length === 0;
    });

    document.dispatchEvent(new CustomEvent('sw:wishlist-changed', { detail: { count: list.length } }));
  };

  document.addEventListener('click', (event) => {
    const btn = event.target instanceof Element ? event.target.closest('[data-sw-wish]') : null;
    if (!btn) return;

    // Cards wrap the heart in a link to the product — never navigate on a save.
    event.preventDefault();
    event.stopPropagation();

    const handle = btn.dataset.swWish;
    if (!handle) return;

    const list = swWishlistRead();
    const idx = list.indexOf(handle);

    if (idx === -1) list.push(handle);
    else list.splice(idx, 1);

    swWishlistWrite(list);
    paint();
  });

  // Cart drawer / cart page → "Move to wishlist". This half only records the
  // save; the button carries the cart component's own `on:click` remove binding,
  // so propagation must continue. Capture phase, because that removal detaches
  // the button and everything above it before the bubble phase gets here.
  document.addEventListener(
    'click',
    (event) => {
      const btn = event.target instanceof Element ? event.target.closest('[data-sw-cart-wish]') : null;
      if (!btn) return;

      const handle = btn.dataset.swCartWish;
      if (!handle) return;

      const list = swWishlistRead();
      // Already saved is not an error — the line still leaves the cart.
      if (!list.includes(handle)) {
        list.push(handle);
        swWishlistWrite(list);
      }

      paint();
    },
    { capture: true }
  );

  /* ---- Wishlist → cart -------------------------------------------------
     The card's own quick add does the adding: one-size products go straight
     to the cart, sized ones open the size picker. That keeps every cart
     concern — events, drawer, fly-to-cart, sold-out states — in Horizon's
     hands instead of a second implementation here. */

  /** The product waiting on a cart add, with how much of it the cart already held. */
  let pendingMove = null;

  const cartQuantityOf = async (handle) => {
    try {
      const response = await fetch(`${SW_ROOT}cart.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) return null;
      const cart = await response.json();
      return cart.items.reduce((total, item) => (item.handle === handle ? total + item.quantity : total), 0);
    } catch {
      return null;
    }
  };

  document.addEventListener('click', async (event) => {
    const btn = event.target instanceof Element ? event.target.closest('[data-sw-move-to-cart]') : null;
    if (!btn) return;

    event.preventDefault();

    const handle = btn.dataset.swMoveToCart;
    const quickAdd = btn.closest('.sw-pcard')?.querySelector('quick-add-component');
    if (!handle || !quickAdd) return;

    // 'add' submits the card's form; 'choose' opens the size picker.
    const wanted = quickAdd.dataset.quickAddButton === 'add' ? '--add' : '--choose';
    const trigger =
      quickAdd.querySelector(`.quick-add__button${wanted}`) ?? quickAdd.querySelector('.quick-add__button');
    if (!trigger) return;

    pendingMove = { handle, before: await cartQuantityOf(handle) };
    trigger.click();
  });

  // Unsave only once the cart really gained the product. Opening the size
  // picker and closing it without adding must leave the wishlist alone, and
  // that is exactly the case where no add event ever fires.
  document.addEventListener('shopify:cart:lines-update', async (event) => {
    if (!pendingMove || event.action !== 'add') return;

    const { handle, before } = pendingMove;
    pendingMove = null;

    try {
      // Settle the mutation first, so the cart read below sees the new line.
      await event.promise;
    } catch {
      return;
    }

    const after = await cartQuantityOf(handle);
    if (before === null || after === null || after <= before) return;

    const list = swWishlistRead();
    const idx = list.indexOf(handle);
    if (idx === -1) return;

    list.splice(idx, 1);
    swWishlistWrite(list);
    // paint() fires sw:wishlist-changed, which drops the card from the grid.
    paint();
  });

  swReady(paint);
  document.addEventListener('shopify:section:load', paint);
  document.addEventListener('sw:cards-rendered', paint);
});

/* ==========================================================================
   Wishlist page — renders saved products through the Section Rendering API
   ========================================================================== */

swSafe('wishlist-page', () => {
  const render = async (root) => {
    const grid = root.querySelector('[data-sw-wishlist-grid]');
    const empty = root.querySelector('[data-sw-wishlist-empty]');
    if (!grid) return;

    const handles = swWishlistRead();
    const cardSection = root.dataset.cardSection || 'swishin-recently-viewed-card';

    const show = (hasItems) => {
      grid.hidden = !hasItems;
      if (empty) empty.hidden = hasItems;
      root.dataset.state = hasItems ? 'filled' : 'empty';
    };

    if (handles.length === 0) {
      grid.innerHTML = '';
      show(false);
      return;
    }

    const results = await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(`${SW_ROOT}products/${handle}?section_id=${cardSection}`);
          // A product that was deleted or unpublished simply drops out.
          if (!response.ok) return '';
          return swCardMarkup(await response.text());
        } catch {
          return '';
        }
      })
    );

    const html = results.filter(Boolean).join('');
    grid.innerHTML = html;
    show(Boolean(html.trim()));
    document.dispatchEvent(new CustomEvent('sw:cards-rendered'));
  };

  const run = () => document.querySelectorAll('[data-sw-wishlist]').forEach(render);

  swReady(run);
  document.addEventListener('shopify:section:load', run);

  // Removing the last item from the page itself should empty it immediately.
  document.addEventListener('sw:wishlist-changed', () => {
    document.querySelectorAll('[data-sw-wishlist]').forEach((root) => {
      const remaining = swWishlistRead();
      root.querySelectorAll('[data-sw-wish]').forEach((btn) => {
        if (!remaining.includes(btn.dataset.swWish)) {
          btn.closest('.sw-pcard')?.remove();
        }
      });
      if (remaining.length === 0) render(root);
    });
  });
});

/* ==========================================================================
   Viewer count — randomised per visitor, stable for the session
   ========================================================================== */

swSafe('viewers', () => {
  const pick = (el) => {
    const min = parseInt(el.dataset.min || '8', 10);
    const max = parseInt(el.dataset.max || '30', 10);
    const handle = el.dataset.handle || 'x';
    const key = `sw:viewers:${handle}`;

    let value = null;
    try {
      value = sessionStorage.getItem(key);
    } catch {
      /* sessionStorage unavailable — fall through to a fresh number */
    }

    if (!value) {
      const span = Math.max(1, max - min + 1);
      value = String(min + Math.floor(Math.random() * span));
      try {
        sessionStorage.setItem(key, value);
      } catch {
        /* ignore */
      }
    }

    const text = el.querySelector('[data-sw-viewers-text]');
    const template = el.dataset.template || '[count] people viewing this';
    if (text) text.textContent = template.replace('[count]', value);
  };

  const run = () => document.querySelectorAll('[data-sw-viewers]').forEach(pick);

  swReady(run);
  document.addEventListener('shopify:section:load', run);
});

/* ==========================================================================
   Recently viewed — records handles, then renders cards via the
   Section Rendering API so pricing and badges come from Liquid, not JS
   ========================================================================== */

swSafe('recently-viewed', () => {
  const KEY = 'sw:recentlyViewed';
  const MAX = 12;

  const read = () => {
    try {
      const value = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(value) ? value.filter((h) => typeof h === 'string') : [];
    } catch {
      return [];
    }
  };

  const record = (handle) => {
    if (!handle) return;
    const list = read().filter((h) => h !== handle);
    list.unshift(handle);

    try {
      localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
    } catch {
      /* ignore */
    }
  };

  const script = document.getElementById('sw-script');
  const currentHandle = script ? script.dataset.product : '';

  const render = async (section) => {
    const grid = section.querySelector('[data-sw-recently-viewed-grid]');
    if (!grid) return;

    const limit = parseInt(section.dataset.limit || '6', 10);
    const exclude = section.dataset.exclude || '';
    const cardSection = section.dataset.cardSection || 'swishin-recently-viewed-card';

    // Read before the current product is recorded so the page you are on is not listed.
    const handles = read()
      .filter((handle) => handle !== exclude)
      .slice(0, limit);

    if (handles.length === 0) return;

    const results = await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(`${SW_ROOT}products/${handle}?section_id=${cardSection}`);
          if (!response.ok) return '';
          return swCardMarkup(await response.text());
        } catch {
          return '';
        }
      })
    );

    const html = results.filter(Boolean).join('');
    if (!html.trim()) return;

    grid.innerHTML = html;
    section.dataset.state = 'filled';
    document.dispatchEvent(new CustomEvent('sw:cards-rendered'));
  };

  swReady(() => {
    document.querySelectorAll('[data-sw-recently-viewed]').forEach((section) => {
      // The editor renders real cards server side — leave those alone.
      if (section.dataset.state === 'filled') return;
      render(section);
    });

    // Recorded last so the section above never includes the current product.
    record(currentHandle);
  });

  document.addEventListener('click', (event) => {
    const btn = event.target instanceof Element ? event.target.closest('[data-sw-recently-viewed-clear]') : null;
    if (!btn) return;

    try {
      localStorage.removeItem(KEY);
    } catch {
      /* ignore */
    }

    const section = btn.closest('[data-sw-recently-viewed]');
    if (section) section.dataset.state = 'empty';
  });
});

/* ==========================================================================
   Complete the look — pulls personalised recommendations after first paint
   ========================================================================== */

swSafe('recommendations', () => {
  const load = async (section) => {
    const url = section.dataset.url;
    const productId = section.dataset.productId;
    const grid = section.querySelector('[data-sw-recommendations-grid]');

    if (!url || !productId || !grid) return;

    try {
      const response = await fetch(`${url}&product_id=${productId}`);
      if (!response.ok) return;

      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const incoming = doc.querySelector('[data-sw-recommendations-grid]');

      // An empty response means the product has no recommendations yet — keep
      // whatever fallback Liquid already rendered rather than blanking it out.
      if (!incoming || !incoming.innerHTML.trim()) return;

      grid.innerHTML = incoming.innerHTML;
      document.dispatchEvent(new CustomEvent('sw:cards-rendered'));
    } catch {
      /* keep the server-rendered fallback */
    }
  };

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        obs.unobserve(entry.target);
        load(entry.target);
      });
    },
    { rootMargin: '0px 0px 500px 0px' }
  );

  swReady(() => {
    document.querySelectorAll('[data-sw-recommendations]').forEach((section) => {
      if (section.dataset.source === 'collection') return;
      if (section.dataset.performed === 'true') return;
      observer.observe(section);
    });
  });
});

/* ==========================================================================
   Delivery checker — prefix rules evaluated locally, no network call
   ========================================================================== */

swSafe('pincode', () => {
  const parseRules = (raw) => {
    const rules = [];

    (raw || '')
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean)
      .forEach((part) => {
        const [prefix, days] = part.split(':').map((value) => (value || '').trim());
        if (!/^\d+$/.test(prefix)) return;
        rules.push({ prefix, days: parseInt(days, 10) || 0 });
      });

    // Longest prefix first so 400:2 beats 40:3.
    return rules.sort((a, b) => b.prefix.length - a.prefix.length);
  };

  const formatDate = (days) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date.toLocaleDateString(document.documentElement.lang || 'en-IN', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  };

  const check = (widget) => {
    const input = widget.querySelector('[data-sw-pincode-input]');
    const result = widget.querySelector('[data-sw-pincode-result]');
    if (!input || !result) return;

    const pincode = (input.value || '').replace(/\D/g, '');

    const show = (message, state) => {
      result.textContent = message;
      result.dataset.state = state;
      result.hidden = false;
    };

    if (pincode.length !== 6) {
      show(widget.dataset.invalid || 'Please enter a valid 6-digit pincode.', 'error');
      return;
    }

    const rules = parseRules(widget.dataset.rules);
    const match = rules.find((rule) => pincode.startsWith(rule.prefix));
    const serviceableOnly = widget.dataset.serviceableOnly === 'true';

    if (!match && serviceableOnly) {
      show(widget.dataset.unserviceable || 'We do not deliver to this pincode yet.', 'error');
      return;
    }

    const days = (match && match.days) || parseInt(widget.dataset.defaultDays || '6', 10);

    const codPrefixes = (widget.dataset.codPrefixes || '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);

    const codAvailable = codPrefixes.length === 0 || codPrefixes.some((prefix) => pincode.startsWith(prefix));

    const message = (widget.dataset.success || 'Delivers by [date] · [days] days')
      .replace('[date]', formatDate(days))
      .replace('[days]', String(days));

    const suffix = codAvailable ? widget.dataset.codYes : widget.dataset.codNo;

    show(suffix ? `${message} · ${suffix}` : message, 'ok');
  };

  document.addEventListener('click', (event) => {
    const btn = event.target instanceof Element ? event.target.closest('[data-sw-pincode-submit]') : null;
    if (!btn) return;

    const widget = btn.closest('[data-sw-pincode]');
    if (widget) check(widget);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;

    const input = event.target instanceof Element ? event.target.closest('[data-sw-pincode-input]') : null;
    if (!input) return;

    event.preventDefault();
    const widget = input.closest('[data-sw-pincode]');
    if (widget) check(widget);
  });
});

/* ==========================================================================
   Keep the custom PDP blocks in sync with variant changes

   Horizon's variant picker fetches the whole product page and hands the parsed
   document to listeners of `shopify:product:select`. The stock blocks morph
   themselves from it; these custom blocks do the same so price, savings and
   stock never go stale after switching colour or size.
   ========================================================================== */

swSafe('variant-sync', () => {
  const SELECTORS = ['[data-sw-price]', '.sw-savings', '.sw-urgency'];

  document.addEventListener('shopify:product:select', (event) => {
    // Product card swatches fire the same event — only the main picker should
    // rewrite the product details column.
    if (event.target instanceof Element && event.target.closest('product-card')) return;
    if (!event.promise) return;

    event.promise
      .then(({ detail }) => {
        if (!detail || !detail.html) return;

        SELECTORS.forEach((selector) => {
          const current = document.querySelector(`.product-details ${selector}`);
          const incoming = detail.html.querySelector(`.product-details ${selector}`);

          if (!current || !incoming) return;
          if (current.innerHTML === incoming.innerHTML) return;

          current.innerHTML = incoming.innerHTML;
        });
      })
      .catch(() => {
        /* the picker already logs fetch failures */
      });
  });
});

/* ==========================================================================
   Variant picker extras — size guide link and relabelled legends
   ========================================================================== */

swSafe('variant-extras', () => {
  const decorate = (config) => {
    const picker = config.closest('.product-details, .shopify-section, body').querySelector('.variant-picker');
    if (!picker) return;

    const sizeOption = (config.dataset.sizeOption || 'Size').trim().toLowerCase();
    const sizeLabel = config.dataset.sizeLabel || '';
    const guideLabel = config.dataset.guideLabel || '';
    const guideUrl = config.dataset.guideUrl || '';

    picker.querySelectorAll('fieldset legend').forEach((legend) => {
      if (legend.dataset.swDecorated === 'true') return;

      // The legend holds the option name as a bare text node followed by the
      // selected value in a span — only the text node should be relabelled.
      const textNode = [...legend.childNodes].find(
        (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim().length > 0
      );
      if (!textNode) return;

      const optionName = textNode.textContent.trim().toLowerCase();
      if (optionName !== sizeOption) return;

      legend.dataset.swDecorated = 'true';

      if (sizeLabel) textNode.textContent = sizeLabel;

      if (guideLabel && guideUrl) {
        const link = document.createElement('a');
        link.className = 'sw-size-guide';
        link.href = guideUrl;
        link.textContent = guideLabel;
        legend.appendChild(link);
      }
    });
  };

  const run = () => document.querySelectorAll('[data-sw-variant-extras]').forEach(decorate);

  swReady(run);
  document.addEventListener('shopify:section:load', run);

  // The picker replaces its own markup after a variant change, which drops the
  // injected link — re-decorate once the morph has settled.
  document.addEventListener('shopify:product:select', (event) => {
    if (!event.promise) {
      requestAnimationFrame(run);
      return;
    }

    event.promise.then(() => requestAnimationFrame(run)).catch(() => {});
  });
});
